import dayjs, { type Dayjs } from 'dayjs';
import { useMemo } from 'react';
import { AddDialog } from '@/components/AddDialog';
import { EntityEditTagsField } from '@/features/shared/components/entityDetails/EntityEditFields';
import { getFilterEmptyLabel, getFilterLabel } from '@/shared/copy/filters';
import { getFormLabel } from '@/shared/copy/forms';
import { DialogFormBody } from '@/ui';
import { SingleSelectFilterField } from '@/ui/composites/filters/SingleSelectFilterField';
import { withStatusFilterOptionIcons } from '@/ui/composites/filters/statusFilterOptionIcons';
import { optionalLabel } from '@/ui/hooks/validation';
import { DatePicker } from '@/ui/primitives/DatePicker';
import { TextField } from '@/ui/primitives/TextField';
import {
  type ReleaseFormErrors,
  type ReleaseFormHandlers,
  type ReleaseFormValues,
  type ProjectOption,
  type StatusOption,
} from './types';
import type { ReleaseDetailDto } from '@flowqa/shared';
import type { ReactElement } from 'react';

type AddReleaseDialogProps = {
  open: boolean;
  title: string;
  saving?: boolean;
  values: ReleaseFormValues;
  errors?: ReleaseFormErrors;
  statusOptions: StatusOption[];
  projectOptions: ProjectOption[];
  showProjectSelect: boolean;
  showStatusSelect: boolean;
  contextText?: string;
  errorMessage?: string | null;
  onClose: () => void;
  onSave: () => void;
  handlers: ReleaseFormHandlers;
};

export const AddReleaseDialog = ({
  open,
  title,
  saving = false,
  values,
  errors,
  statusOptions,
  projectOptions,
  showProjectSelect,
  showStatusSelect,
  contextText,
  errorMessage,
  onClose,
  onSave,
  handlers,
}: AddReleaseDialogProps): ReactElement => {
  const plannedForLabel = optionalLabel('Planned For');
  const releasedAtLabel = optionalLabel('Released At');
  const Field = TextField;
  const toDayjs = (value: string): Dayjs | null => (value ? dayjs(value) : null);
  const toDateString = (value: Dayjs | null): string => (value ? value.format('YYYY-MM-DD') : '');
  const statusOptionsWithIcons = useMemo(
    () =>
      withStatusFilterOptionIcons(
        statusOptions.map((status) => ({
          value: status.value,
          label: status.label,
        })),
      ),
    [statusOptions],
  );

  return (
    <AddDialog
      open={open}
      title={title}
      saving={saving}
      description={contextText}
      errorMessage={errorMessage}
      onClose={onClose}
      onSave={onSave}
    >
      <DialogFormBody>
        <Field
          label={getFormLabel('name')}
          value={values.name}
          onChange={(event) => {
            handlers.onNameChange(event.target.value);
          }}
          fieldStyle="settings"
          required
          autoFocus
          {...(errors?.name ?? {})}
        />
        {showProjectSelect ? (
          <SingleSelectFilterField
            labelPlacement="stacked"
            required
            label={getFilterLabel('project')}
            value={values.projectId}
            fullWidth
            emptyLabel={getFilterEmptyLabel('selectProject')}
            options={projectOptions.map((project) => ({
              value: project.id,
              label: project.name,
            }))}
            helperText={errors?.projectId?.helperText}
            error={errors?.projectId?.error}
            onChange={(nextValue) => {
              handlers.onProjectChange?.(nextValue);
            }}
          />
        ) : null}
        {showStatusSelect ? (
          <SingleSelectFilterField
            labelPlacement="stacked"
            label={getFormLabel('status')}
            value={values.status}
            fullWidth
            options={statusOptionsWithIcons}
            helperText={errors?.status?.helperText}
            error={errors?.status?.error}
            onChange={(nextValue) => {
              handlers.onStatusChange?.(nextValue as ReleaseDetailDto['status']);
            }}
          />
        ) : null}
        <DatePicker
          label={plannedForLabel}
          value={toDayjs(values.plannedFor)}
          onChange={(nextValue) => {
            handlers.onPlannedForChange(toDateString(nextValue));
          }}
        />
        <DatePicker
          label={releasedAtLabel}
          value={toDayjs(values.releasedAt)}
          onChange={(nextValue) => {
            handlers.onReleasedAtChange(toDateString(nextValue));
          }}
        />
        <EntityEditTagsField
          open={open}
          projectId={values.projectId}
          tags={values.tags ?? []}
          helperText="Tags will be applied only to this release."
          onTagsChange={(nextTags) => {
            handlers.onTagsChange?.(nextTags);
          }}
        />
      </DialogFormBody>
    </AddDialog>
  );
};
